import { z } from "zod";
import { defineStepConfig } from "../../../registry/helpers";
import { entityOfType } from "../../../utils/system-entities";
import { SystemEntity } from "@erp/shared-types";

/**
 * Resolve Invoice References Step
 *
 * Attempts to match invoice numbers from the remittance advice
 * to existing AR invoices and creates payment applications.
 *
 * Note: This is a backend-only step that requires database access.
 * The handler is registered in backend/src/workflows/steps/handlers/
 */

const invoiceReferenceSchema = z.object({
  invoiceNumber: z.string(),
  appliedAmount: z.number().optional(),
  grossAmount: z.number().optional(),
  discountAmount: z.number().optional(),
  adjustmentAmount: z.number().optional(),
});

// Standalone (non-invoice) deductions passed through from the remittance doc.
// Persisted as customer-scoped deduction line items alongside the matched
// invoice references.
const deductionSchema = z.object({
  amount: z.number(),
  reason: z.string().optional(),
});

export const resolveInvoiceReferencesStep = defineStepConfig({
  type: "resolve_invoice_references",
  name: "Resolve Invoice References",
  description:
    "Match invoice references from remittance to existing AR invoices",
  category: "Data Access",
  libraryModule: "AccountsReceivable",
  inputSchema: z.object({
    status: z
      .enum(["success", "extraction_failed", "customer_unmatched"])
      .describe("Status from the upstream create-pending-payment step"),
    payment: entityOfType(SystemEntity.AR_PAYMENT)
      .optional()
      .describe(
        "The AR payment entity, present when a Payment was materialized",
      ),
    paymentId: z
      .uuid()
      .optional()
      .describe("Payment ID, present when a Payment was materialized"),
    remittanceAdviceId: z
      .uuid()
      .optional()
      .describe(
        "Remittance advice owning the claimed invoice references. Required to record remittance_line_item rows; absent when extraction failed.",
      ),
    invoiceReferences: z.array(invoiceReferenceSchema),
    deductions: z.array(deductionSchema).optional(),
  }),

  outputSchema: z.object({
    resolvedCount: z.number(),
    unresolvedCount: z.number(),
    remittanceLineItemIds: z.array(z.uuid()),
    unresolvedInvoiceNumbers: z.array(z.string()),
  }),

  configSchema: z.object({
    fuzzyMatch: z
      .boolean()
      .optional()
      .describe(
        "Enable fuzzy matching for invoice numbers (e.g., partial matches, prefix matching)",
      ),
  }),

  emits: [] as const,
});
